import { CampaignOutlined } from "@mui/icons-material";
import { Box } from "@mui/material";
import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { SystemDropdown } from "../../../../../components/UIToolkit/Inputs/SystemDropDown";
import { InlineHint } from "../../../../../components/UIToolkit/molecules/InlineHint";
import { ReadOnlyMetadataField } from "../../../../../components/UIToolkit/molecules/ReadOnlyMetadataField";
import { SystemBanner } from "../../../../../components/UIToolkit/molecules/SystemBanner";
import type { ClipboardFacilityOption } from "./clipboardOnboardingTypes";
import { CLIPBOARD_STEP_COLUMNS_SX, CLIPBOARD_STEP_FORM_COLUMN_SX } from "./clipboardWizardLayout";

type Props = {
	facilities: ClipboardFacilityOption[];
	selectedFacilityId: string | null;
	onSelect: (facilityId: string | null) => void;
	/** The department's own timezone, to flag a facility whose Clipboard timezone disagrees. */
	departmentTimezone: string;
	aside: ReactNode;
};

/**
 * Step 2 — pick which of the login's workplaces this department is.
 *
 * A facility another department already holds renders **disabled**, naming its owner: the unique index
 * would reject the write anyway, and preventing the collision reads better than reporting a 409 after
 * the fact. The warning is not decoration — saving a facility id is what opens outbound posting to a
 * live agency marketplace, and that is not obvious from the field's name.
 */
export const ClipboardFacilityStep: FC<Props> = ({ facilities, selectedFacilityId, onSelect, departmentTimezone, aside }) => {
	const { t } = useTranslation();
	const selected = facilities.find((facility) => facility.facilityId === selectedFacilityId) ?? null;
	// Clipboard's timezone for the building is informational: shift times are still built from the
	// department's own timezone, so a mismatch is a "check the department" signal, never a blocker.
	const timezoneMismatch = !!selected?.tmz && !!departmentTimezone && selected.tmz !== departmentTimezone;

	return (
		<Box sx={CLIPBOARD_STEP_COLUMNS_SX}>
			<Box sx={CLIPBOARD_STEP_FORM_COLUMN_SX}>
				<SystemDropdown<string>
					label={t("department.config.clipboard.wizard.facility.label")}
					fullWidth
					search
					items={facilities.map((facility) => ({
						value: facility.facilityId,
						label: `${facility.name} · ${facility.facilityId}`,
						selectedLabel: `${facility.name} · ${facility.facilityId}`,
						secondaryLabel: facility.takenByDepartmentId
							? t("department.config.clipboard.wizard.facility.taken", { department: facility.takenByDepartmentId })
							: undefined,
						disabled: !!facility.takenByDepartmentId,
					}))}
					selectedItem={selectedFacilityId ?? undefined}
					onChange={(value) => onSelect(value ?? null)}
					data-testid="clipboard-wizard-facility-select"
				/>
				{selected?.tmz && (
					<ReadOnlyMetadataField
						label={t("department.config.clipboard.wizard.facility.timezone")}
						value={selected.tmz}
						dataTestId="clipboard-wizard-facility-timezone"
					/>
				)}
				{timezoneMismatch && (
					<InlineHint tone="warning" dataTestId="clipboard-wizard-facility-timezone-mismatch">
						{t("department.config.clipboard.wizard.facility.timezoneMismatch", { facility: selected?.tmz, department: departmentTimezone })}
					</InlineHint>
				)}
				<SystemBanner
					tone="warning"
					icon={<CampaignOutlined />}
					title={t("department.config.clipboard.wizard.facility.warning.title")}
					description={t("department.config.clipboard.wizard.facility.warning.description")}
					dataTestId="clipboard-wizard-facility-warning"
				/>
			</Box>
			{aside}
		</Box>
	);
};
